import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FaceProcessor from '../components/FaceProcessor.jsx';
import FaceGuideOverlay from '../components/FaceGuideOverlay';
import PageFooter from '../components/PageFooter';
import MagnifiedScalpUpload from '../components/MagnifiedScalpUpload';
import { processImageFile } from '../utils/imageFileUtils';
import { calculateFaceGuidance, calculateSideFaceGuidance, calculateGuideBounds } from '../utils/faceGuidanceLogic';
import { initializeSession, getSessionIdFromUrl, getSessionId, pathWithSessionId, SESSION_STORAGE_KEY } from '../utils/sessionManager';
import { useHairSession } from '../context/HairSessionContext';
import { useHairAnalysisPlan } from '../context/HairAnalysisPlanContext';
import { getApiBaseUrl } from '../utils/apiConfig';
import HairDisclaimerModal from '../components/HairDisclaimerModal';
import { HAIR_DISCLAIMER_KEY } from '../config/hairApp';
import {
  navigateHairFlowHome,
  resolveHairGuestUserDataForAnalysis,
} from '../utils/hairSalonFlowDeps';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './HairCapturePage.css';

const STEP_ORDER = ['crown', 'left'];

const STEP_LABELS = {
  crown: 'Crown Area',
  left: 'Side View',
};

function readHairDisclaimerAccepted(locationState) {
  if (locationState?.disclaimerAccepted) return true;
  try {
    return sessionStorage.getItem(HAIR_DISCLAIMER_KEY) === '1';
  } catch (_) {
    return false;
  }
}

const HairCapturePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useHairSession();
  const { isReady: isCaptureModeReady } = useHairAnalysisPlan();

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const frameTimerRef = useRef(null);
  const frameLoopActiveRef = useRef(false);
  const frameInFlightRef = useRef(false);
  const faceDetectedRef = useRef(false);
  const hasAutoCapturedRef = useRef(false);
  const lastLandmarksRef = useRef(null);
  const lastVideoDimensionsRef = useRef(null);
  const handleCaptureRef = useRef(null);
  const isCameraInitializingRef = useRef(false);
  const captureStepRef = useRef('crown');
  const uploadInputRef = useRef(null);
  const uploadTargetStepRef = useRef('crown');

  // Detect device type and set appropriate camera config
  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile'; // Mobile devices
    if (width < 1024) return 'tablet'; // Tablets
    if (width < 1440) return 'small-laptop'; // Small laptops
    return 'desktop'; // Desktop and large screens
  };

  // Frame config - responsive based on device type
  const getFrameConfig = () => {
    const deviceType = getDeviceType();
    const isMobile = deviceType === 'mobile';

    // Base config for all devices - always use selfie camera (user facingMode)
    const baseConfig = {
      facingMode: 'user', // Always use selfie camera (front-facing)
      audio: false,
    };

    if (isMobile) {
      // Mobile: Use 720p for better stability on iOS/Android
      // 1080p can cause tab crashes ("can't open page") on some iOS devices
      return {
        width: 1280,
        minDelay: 220,
        quality: 0.85,
        videoConstraints: {
          ...baseConfig,
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 30 },
        },
      };
    } else if (deviceType === 'tablet') {
      // Tablet: 1080p for stability
      return {
        width: 1920,
        minDelay: 220,
        quality: 0.9,
        videoConstraints: {
          ...baseConfig,
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30, max: 60 },
        },
      };
    } else if (deviceType === 'small-laptop') {
      // Small laptop: 1080p or 1440p
      return {
        width: 1920,
        minDelay: 220,
        quality: 0.86,
        videoConstraints: {
          ...baseConfig,
          width: { ideal: 2560, max: 2560 },
          height: { ideal: 1440, max: 1440 },
          frameRate: { ideal: 30, max: 60 },
        },
      };
    } else {
      // Desktop: Keep high resolution but cap snapshot at reasonable limit
      return {
        width: 1920,
        minDelay: 220,
        quality: 0.88,
        videoConstraints: {
          ...baseConfig,
          width: { ideal: 3840, max: 3840 },
          height: { ideal: 2160, max: 2160 },
          frameRate: { ideal: 60, max: 60 },
        },
      };
    }
  };

  const frameConfigRef = useRef(getFrameConfig());

  // State
  const [faceGuidance, setFaceGuidance] = useState({
    isInsideFrame: false,
    statusColor: 'red',
    message: 'Position your face',
  });
  const [isCapturing, setIsCapturing] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(true);
  // Session: state > URL (?session_id=) > localStorage so flow works when state is lost
  const [sessionId, setSessionId] = useState(() => location.state?.sessionId ?? getSessionIdFromUrl(location) ?? getSessionId() ?? null);
  const [saveError, setSaveError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isProceeding, setIsProceeding] = useState(false);
  // Unified capture: 'crown' | 'left' | 'done'.
  const [hairCaptures, setHairCaptures] = useState({
    crown: null,
    left: null,
  });
  const [showReview, setShowReview] = useState(false);
  const [lastCapturedImage, setLastCapturedImage] = useState(null);
  const [isFromFinalReview, setIsFromFinalReview] = useState(false);
  const [captureStep, setCaptureStep] = useState('crown');
  const [magnifiedScalp, setMagnifiedScalp] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() =>
    readHairDisclaimerAccepted(location.state)
  );
  // Sync captureStepRef
  useEffect(() => {
    captureStepRef.current = captureStep;
  }, [captureStep]);


  // Start camera
  const startCamera = useCallback(async () => {
    if (typeof navigator === 'undefined') {
      return;
    }
    if (mediaStreamRef.current) {
      // If stream exists but video is paused (e.g. after capture), resume it
      if (videoRef.current && videoRef.current.paused) {
        try {
          await videoRef.current.play();
        } catch (e) {
          console.error("Error resuming video:", e);
        }
        setPreviewEnabled(true);
      }
      return;
    }

    if (isCameraInitializingRef.current) {
      return;
    }

    isCameraInitializingRef.current = true;

    try {
      const config = frameConfigRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: config.videoConstraints,
        audio: false,
      });

      // Check if we should still be running (e.g. unmounted/stopped while waiting)
      if (!isCameraInitializingRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      mediaStreamRef.current = stream;
      const videoEl = videoRef.current;

      if (videoEl) {
        try {
          if (!videoEl.paused) {
            await videoEl.pause();
          }
        } catch (_) {
          /* ignore */
        }
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('muted', 'true');
        videoEl.muted = true;
        videoEl.srcObject = stream;
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          await playPromise.catch((error) => {
            if (error && error.name !== 'AbortError') {
              throw error;
            }
          });
        }
        setPreviewEnabled(true);
      }
    } catch (error) {
      if (isCameraInitializingRef.current) {
        setPreviewEnabled(false);
        let errorMessage = 'Unable to access camera. ';
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          errorMessage += 'Please grant camera permissions.';
        } else if (error.name === 'NotFoundError') {
          errorMessage += 'No camera found.';
        } else {
          errorMessage += 'Please check your camera settings.';
        }
        alert(errorMessage);
      }
    } finally {
      isCameraInitializingRef.current = false;
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    isCameraInitializingRef.current = false;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (err) {
      }
    }
    setPreviewEnabled(false);
  }, []);

  // Stop frame loop
  const stopFrameLoop = useCallback(() => {
    frameLoopActiveRef.current = false;
    if (frameTimerRef.current) {
      clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
    frameInFlightRef.current = false;
  }, []);

  // Send frame for processing
  const sendFrame = useCallback(async () => {
    if (
      !videoRef.current ||
      !canvasRef.current ||
      !mediaStreamRef.current ||
      frameInFlightRef.current
    ) {
      return false;
    }
    const videoEl = videoRef.current;
    if (videoEl.readyState < 2) {
      return false;
    }

    const canvasEl = canvasRef.current;
    const { width: targetWidth } = frameConfigRef.current || {};
    const WIDTH = targetWidth || 3840;
    const HEIGHT =
      Math.round((videoEl.videoHeight / videoEl.videoWidth) * WIDTH) ||
      Math.round((9 / 16) * WIDTH) ||
      2160;

    canvasEl.width = WIDTH;
    canvasEl.height = HEIGHT;
    const ctx = canvasEl.getContext('2d', { willReadFrequently: true, alpha: false });
    if (!ctx) {
      return false;
    }
    ctx.drawImage(videoEl, 0, 0, WIDTH, HEIGHT);

    return true;
  }, []);

  // Frame loop
  const runFrameLoop = useCallback(async () => {
    if (!frameLoopActiveRef.current) {
      return;
    }
    const getNow =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? () => performance.now()
        : () => Date.now();
    const startedAt = getNow();
    const sent = await sendFrame();
    const finishedAt = getNow();
    const elapsed = finishedAt - startedAt;

    const config = frameConfigRef.current || {};
    const baseDelay = config.minDelay || 220;
    const fallbackDelay = baseDelay + 120;
    const dynamicDelay = sent ? Math.max(baseDelay, elapsed * 1.25) : fallbackDelay;
    const nextDelay = Math.min(Math.max(dynamicDelay, baseDelay), baseDelay + 800);

    if (!frameLoopActiveRef.current) {
      return;
    }
    frameTimerRef.current = setTimeout(runFrameLoop, nextDelay);
  }, [sendFrame]);

  // Start frame loop
  const startFrameLoop = useCallback(async () => {
    if (captureStepRef.current === 'done') return;
    await startCamera();
    if (frameLoopActiveRef.current) {
      return;
    }
    frameLoopActiveRef.current = true;
    frameTimerRef.current = setTimeout(runFrameLoop, 0);
  }, [startCamera, runFrameLoop]);

  // Handle face landmarks from MediaPipe
  const handleLandmarksDetected = useCallback((landmarks, videoDimensions) => {
    const faceDetected = Boolean(landmarks && landmarks.length > 0);
    faceDetectedRef.current = faceDetected;

    // Store landmarks for cropping
    if (landmarks && landmarks.length > 0) {
      lastLandmarksRef.current = landmarks;
      lastVideoDimensionsRef.current = videoDimensions;
    }

    if (landmarks && videoDimensions) {
      const ovalBounds = calculateGuideBounds(videoDimensions.width, videoDimensions.height);
      
      let guidance;
      if (captureStep === 'left') {
        guidance = calculateSideFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          captureStep,
          true, // lightingOk forced to true as requested
          ''
        );
      } else {
        guidance = calculateFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          ovalBounds,
          true, // lightingOk forced to true as requested
          ''
        );
      }
      
      // Override message with hair-specific position instructions
      const hairInstructions = {
        crown: "Crown View: Tilt your head down to show the top of your head.",
        left: "Side View: Turn your head to show the side of your hair.",
      };
      
      if (hairInstructions[captureStep]) {
        guidance.message = hairInstructions[captureStep];
      }

      setFaceGuidance(guidance);
    } else {
      // No landmarks detected
      setFaceGuidance({
        isInsideFrame: false,
        statusColor: 'red',
        message: 'Position your face',
      });
    }
  }, [captureStep]);

  // Calculate face bounding box from landmarks.


  const handleCapture = useCallback(() => {
    if (!isCaptureModeReady || !videoRef.current || !canvasRef.current || isCapturing) {
      return;
    }

    setIsCapturing(true);
    const videoEl = videoRef.current;
    const canvasEl = canvasRef.current;

    const { width: targetWidth, quality } = frameConfigRef.current || {};
    const WIDTH = Math.min(targetWidth || 1920, 1920);
    const HEIGHT =
      Math.round((videoEl.videoHeight / videoEl.videoWidth) * WIDTH) ||
      Math.round((9 / 16) * WIDTH) ||
      1080;

    canvasEl.width = WIDTH;
    canvasEl.height = HEIGHT;
    const ctx = canvasEl.getContext('2d', {
      willReadFrequently: true,
      alpha: false,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    });
    if (!ctx) {
      setIsCapturing(false);
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Mirror the capture to match the mirrored preview (front camera)
    ctx.save();
    ctx.translate(WIDTH, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoEl, 0, 0, WIDTH, HEIGHT);
    ctx.restore();

    let croppedDataUrl = null;
    
    // Use calculateGuideBounds to get the exact area shown in the outline
    const boundingBox = calculateGuideBounds(WIDTH, HEIGHT);

    if (boundingBox) {
      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = Math.round(boundingBox.width);
      cropCanvas.height = Math.round(boundingBox.height);
      const cropCtx = cropCanvas.getContext('2d', {
        willReadFrequently: true,
        alpha: false,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });

      if (cropCtx) {
        cropCtx.imageSmoothingEnabled = true;
        cropCtx.imageSmoothingQuality = 'high';
        
        // Extract the region from the main canvas that matches the guide bounds
        const cropX = boundingBox.centerX - boundingBox.width / 2;
        const cropY = boundingBox.centerY - boundingBox.height / 2;
        
        // Since the main canvas (canvasEl) is already mirrored to match the preview,
        // and calculateGuideBounds is centered, we just crop the center area.
        cropCtx.drawImage(
          canvasEl,
          Math.round(cropX),
          Math.round(cropY),
          Math.round(boundingBox.width),
          Math.round(boundingBox.height),
          0,
          0,
          Math.round(boundingBox.width),
          Math.round(boundingBox.height)
        );
        croppedDataUrl = cropCanvas.toDataURL('image/jpeg', 0.95);
      }
    }

    const finalImage = croppedDataUrl || canvasEl.toDataURL('image/jpeg', quality ?? 0.95);

    // Save image immediately so navigation arrows and state are updated
    setHairCaptures(prev => ({
      ...prev,
      [captureStepRef.current]: finalImage
    }));

    setLastCapturedImage(finalImage);
    setShowReview(true);
    setIsFromFinalReview(false);
    if (videoEl) {
      videoEl.pause();
    }
    setIsCapturing(false);
  }, [isCaptureModeReady, isCapturing]);

  const openUploadPicker = useCallback((step) => {
    if (isUploading) return;
    uploadTargetStepRef.current = step;
    setUploadError(null);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }
  }, [isUploading]);

  const handleUploadedFile = useCallback(async (file) => {
    if (!file) return;

    const step = uploadTargetStepRef.current;
    setUploadError(null);
    setIsUploading(true);

    try {
      const dataUrl = await processImageFile(file);

      setHairCaptures(prev => ({ ...prev, [step]: dataUrl }));

      // Reviewing straight after upload keeps the flow identical to a camera capture.
      if (captureStepRef.current !== 'done') {
        setCaptureStep(step);
        captureStepRef.current = step;
        setLastCapturedImage(dataUrl);
        setShowReview(true);
        setIsFromFinalReview(false);
        if (videoRef.current) {
          videoRef.current.pause();
        }
      }
    } catch (err) {
      setUploadError(err?.message || 'Could not use that image');
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleConfirmNext = useCallback(() => {
    const allStepsCaptured = Object.values(hairCaptures).every(img => img !== null);

    if (isFromFinalReview || allStepsCaptured) {
      setShowReview(false);
      setLastCapturedImage(null);
      setCaptureStep('done');
      setIsFromFinalReview(false);
      return;
    }

    const step = captureStep;
    const videoEl = videoRef.current;

    if (step === 'crown') {
      setCaptureStep('left');
      if (videoEl) {
        videoEl.play().catch(() => {});
      }
    } else if (step === 'left') {
      setPreviewEnabled(false);
      if (videoEl) {
        try {
          videoEl.pause();
          videoEl.srcObject = null;
        } catch (err) {}
      }
      setCaptureStep('done');
      stopFrameLoop();
      stopCamera();
    }
    setShowReview(false);
    setLastCapturedImage(null);
  }, [captureStep, stopFrameLoop, stopCamera, isFromFinalReview, hairCaptures]);

  const handleRetake = useCallback(() => {
    setShowReview(false);
    setLastCapturedImage(null);
    setIsFromFinalReview(false);
    
    // If we were in 'done' state, we need to restart the camera and go back to that step
    if (captureStep === 'done' || !previewEnabled) {
      // Logic to restart camera for specific step
      setPreviewEnabled(true);
      // captureStep is already set to the specific step if we clicked from grid
      startCamera();
    } else if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [captureStep, previewEnabled, startCamera]);

  const handleBackToReview = useCallback(() => {
    setShowReview(false);
    setLastCapturedImage(null);
    setIsFromFinalReview(false);
    setCaptureStep('done');
  }, []);

  const goToPreviousStep = useCallback(() => {
    const stepOrder = STEP_ORDER;
    const currentIndex = stepOrder.indexOf(captureStep);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      if (hairCaptures[prevStep]) {
        setCaptureStep(prevStep);
        setLastCapturedImage(hairCaptures[prevStep]);
      }
    }
  }, [captureStep, hairCaptures]);

  const goToNextStep = useCallback(() => {
    const stepOrder = STEP_ORDER;
    const currentIndex = stepOrder.indexOf(captureStep);
    if (currentIndex < 4) {
      const nextStep = stepOrder[currentIndex + 1];
      if (hairCaptures[nextStep]) {
        setCaptureStep(nextStep);
        setLastCapturedImage(hairCaptures[nextStep]);
      }
    }
  }, [captureStep, hairCaptures]);

  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  }, [handleCapture]);

  useEffect(() => {
    captureStepRef.current = captureStep;
  }, [captureStep]);

  // Sync sessionId from URL when landing with ?session_id= or from storage
  useEffect(() => {
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    if (fromUrl && fromUrl !== sessionId) {
      setSessionId(fromUrl);
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, fromUrl);
      } catch (e) { }
    } else if (fromStorage && !sessionId) {
      setSessionId(fromStorage);
    }
  }, [location, sessionId]);
  // Initialize session on mount only when we don't already have one (e.g. from landing or URL)
  useEffect(() => {
    if (sessionId) return;
    const initSession = async () => {
      const API_BASE_URL = getApiBaseUrl() || '';
      const sid = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
      setSessionId(sid);
    };
    initSession();
  }, [sessionId, token]);

  /** Proceed to hair analysis with whatever user data is already in location state. */
  const handleProceedToAnalysis = async () => {
    setSaveError(null);
    setIsProceeding(true);
    try {
      const existingId = sessionId ?? location.state?.sessionId;
      let finalSessionId = existingId;
      if (!finalSessionId) {
        const API_BASE_URL = process.env.REACT_APP_API_URL;
        if (!API_BASE_URL) {
          setSaveError('Configuration error: API URL not set. Please contact support.');
          setIsProceeding(false);
          return;
        }
        finalSessionId = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
        setSessionId(finalSessionId);
      }

      const resolved = resolveHairGuestUserDataForAnalysis(location.state || {});

      const aqiData = null;
      const userData = {
        name: resolved.name || '',
        mobileNumber: resolved.mobileNumber || '',
        email: resolved.email || '',
        instagramId: location.state?.instagramId || '',
      };

      navigate(pathWithSessionId('/hair-analysis', finalSessionId), {
        state: {
          hairCaptures,
          magnifiedScalp,
          userData: {
            ...userData,
            gender: resolved.gender || null,
            hairType: location.state?.hairType || resolved.skinType || null,
            age: resolved.age ?? location.state?.age ?? null,
          },
          sessionId: finalSessionId,
          aqiData,
          ...(token && { token }),
        },
      });
    } catch (error) {
      setSaveError(error.message || 'Failed to proceed');
    } finally {
      setIsProceeding(false);
    }
  };

  // Initialize camera after disclaimer is accepted
  useEffect(() => {
    if (isCaptureModeReady && disclaimerAccepted) {
      startFrameLoop();
    }
    return () => {
      stopFrameLoop();
      stopCamera();
    };
  }, [isCaptureModeReady, disclaimerAccepted, startFrameLoop, stopFrameLoop, stopCamera]);

  const handleExitCapture = useCallback(() => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  }, [sessionLandingPath, navigate]);

  const handleDisclaimerConfirm = useCallback(() => {
    try {
      sessionStorage.setItem(HAIR_DISCLAIMER_KEY, '1');
    } catch (_) {}
    setDisclaimerAccepted(true);
  }, []);

  const handleDisclaimerCancel = useCallback(() => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  }, [navigate, sessionLandingPath]);

  const handleBack = useCallback(() => {
    // If in final review grid view (done state)
    if (captureStep === 'done') {
      setCaptureStep('left');
      setPreviewEnabled(true);
      startCamera();
      return;
    }

    // If in the review modal for a specific capture
    if (showReview) {
      if (isFromFinalReview) {
        // Just close modal and return to grid
        setShowReview(false);
        setLastCapturedImage(null);
        setCaptureStep('done');
      } else {
        // In active capture flow: go back to camera of current step
        setShowReview(false);
        setLastCapturedImage(null);
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      }
      return;
    }

    // Step navigation
    const stepOrder = STEP_ORDER;
    const currentIndex = stepOrder.indexOf(captureStep);
    
    if (currentIndex > 0) {
      // Move to previous step
      const prevStep = stepOrder[currentIndex - 1];
      setCaptureStep(prevStep);
      
      if (hairCaptures[prevStep]) {
        // Show the review of the previous step so user can see it
        setLastCapturedImage(hairCaptures[prevStep]);
        setShowReview(true);
      } else {
        // Go to camera of previous step if no image yet
        setLastCapturedImage(null);
        setShowReview(false);
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      }
    } else {
      // Already at first step, exit capture flow
      handleExitCapture();
    }
  }, [captureStep, showReview, isFromFinalReview, handleExitCapture, startCamera, hairCaptures]);

  const handleHome = () => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  };




  return (
    <div className="face-detection-page page-bg">
      {!disclaimerAccepted ? (
        <HairDisclaimerModal
          isOpen
          onConfirm={handleDisclaimerConfirm}
          onCancel={handleDisclaimerCancel}
        />
      ) : null}

      <main className="face-detection-page-main" role="presentation">
        <div className="capture-stage">
        <div className="capture-viewport">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`video-preview ${captureStep === 'done' ? 'video-preview--hidden' : ''}`}
        />
        <canvas ref={canvasRef} className="capture-canvas" />

        {disclaimerAccepted && previewEnabled && captureStep !== 'done' && (
          <FaceGuideOverlay
            statusColor={faceGuidance.statusColor}
            message={faceGuidance.message}
            videoWidth={videoRef.current?.videoWidth || 640}
            videoHeight={videoRef.current?.videoHeight || 480}
            showOverlay={true}
            countdown={null}
            lightingOk={true}
            faceDetected={faceDetectedRef.current}
            faceStraight={faceGuidance.isFaceStraight || false}
            headTurnDirection={
              captureStep === 'left' ? 'right' : captureStep === 'crown' ? 'down' : null
            }
          />
        )}

        {disclaimerAccepted && previewEnabled && captureStep !== 'done' && (
          <FaceProcessor
            videoElement={videoRef.current}
            onLandmarksDetected={handleLandmarksDetected}
            enabled={disclaimerAccepted && previewEnabled && captureStep !== 'done'}
          />
        )}

        {/* Reference UI Overlay */}
        {previewEnabled && captureStep !== 'done' && (
          <div className="capture-overlay-container">
            {/* Top Left: Hair Target Badge */}
            <div className="capture-badge">
              <span className="badge-dot" />
              <span className="badge-label">HAIR TARGET:</span>
              <span className="badge-value">
                {captureStep === 'crown' ? 'CROWN AREA' : 'SIDE VIEW'}
              </span>
            </div>

            {/* Top Right: Close Button */}
            <button className="capture-close-btn" onClick={handleHome} aria-label="Close">
              ×
            </button>

            {/* Right Side: Tips Box */}
            <div className="capture-tips-box">
              <h4 className="tips-title">Tips:</h4>
              <ul className="tips-list">
                <li>Expose hair-line fully</li>
                <li>Keep face straight</li>
                <li>Avoid shadows</li>
              </ul>
            </div>

            {/* Bottom Left: Back Button */}
            <button className="capture-overlay-back-btn" onClick={handleBack}>
              ← Back
            </button>
          </div>
        )}

        </div>

        {previewEnabled && captureStep !== 'done' && (
          <div className="capture-controls-container">
            <button
              className={`manual-capture-button ${isCapturing ? 'capturing' : ''}`}
              onClick={handleCapture}
              disabled={isCapturing}
            >
              <div className="capture-button-inner" />
            </button>
            <span className="tap-to-capture-label">Tap to Capture</span>

            <button
              type="button"
              className="capture-upload-button"
              onClick={() => openUploadPicker(captureStep)}
              disabled={isUploading || isCapturing}
            >
              {isUploading ? 'Uploading…' : `Upload ${STEP_LABELS[captureStep]} Photo`}
            </button>
            {uploadError && (
              <p className="capture-upload-error" role="alert">{uploadError}</p>
            )}
          </div>
        )}
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          hidden
          disabled={isUploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUploadedFile(file);
          }}
        />

        {/* Review Scan Screen */}
        {showReview && lastCapturedImage && (
          <div className="review-scan-overlay">
            <div className="review-scan-modal">
              {/* Back Button */}
              <button className="review-modal-back-btn" onClick={handleBack} aria-label="Go back">
                ← Back
              </button>
              <h2 className="review-scan-title">
                Review Scan {
                  captureStep === 'crown' ? '1/2' : '2/2'
                }
              </h2>
              
              <div className="review-scan-navigation-wrapper">
                <button 
                  className="review-nav-btn prev" 
                  onClick={goToPreviousStep}
                  disabled={captureStep === 'crown' || !hairCaptures[STEP_ORDER[STEP_ORDER.indexOf(captureStep) - 1]]}
                  aria-label="Previous scan"
                >
                  ‹
                </button>

                <div className="review-scan-image-container">
                  <img src={lastCapturedImage} alt="Captured scan" className="review-scan-image" />
                  <div className="review-scan-step-label">
                    {STEP_LABELS[captureStep] || 'Crown Area'}
                  </div>
                </div>

                <button 
                  className="review-nav-btn next" 
                  onClick={goToNextStep}
                  disabled={captureStep === 'left' || !hairCaptures[STEP_ORDER[STEP_ORDER.indexOf(captureStep) + 1]]}
                  aria-label="Next scan"
                >
                  ›
                </button>
              </div>

              <div className="review-scan-actions">
                <button className="review-retake-btn" onClick={handleRetake}>
                  Retake
                </button>
                <button className="review-confirm-btn" onClick={handleConfirmNext}>
                  {isFromFinalReview ? 'Confirm' : 'Confirm & Next'}
                </button>
              </div>

              {isFromFinalReview && (
                <button className="review-back-to-review-btn" onClick={handleBackToReview}>
                  Back to Review
                </button>
              )}
            </div>
          </div>
        )}
        {/* Captured Image Preview - when all capture done */}
        {captureStep === 'done' && (
          <div className="captured-preview" onClick={(e) => e.stopPropagation()}>
            <div className="captured-preview__grid">
              {STEP_ORDER.map((key) => (
                <div 
                  key={key} 
                  className="captured-preview__grid-item"
                  onClick={() => {
                    if (!hairCaptures[key]) return;
                    setCaptureStep(key);
                    setLastCapturedImage(hairCaptures[key]);
                    setShowReview(true);
                    setIsFromFinalReview(true);
                  }}
                >
                  <div className="captured-preview__grid-img-wrapper">
                    {hairCaptures[key] ? (
                      <img src={hairCaptures[key]} alt={key} />
                    ) : (
                      <div className="captured-preview__grid-placeholder">No Image</div>
                    )}
                  </div>
                  <span className="captured-preview__grid-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  <button
                    type="button"
                    className="captured-preview__grid-upload"
                    disabled={isUploading || isProceeding}
                    onClick={(e) => {
                      e.stopPropagation();
                      openUploadPicker(key);
                    }}
                  >
                    {hairCaptures[key] ? 'Replace' : 'Upload'}
                  </button>
                </div>
              ))}
            </div>

            {uploadError && (
              <p className="capture-upload-error" role="alert">{uploadError}</p>
            )}

            <MagnifiedScalpUpload
              value={magnifiedScalp}
              onChange={setMagnifiedScalp}
              disabled={isProceeding}
            />

            <div className="capture-actions">
              <button
                className="skin-action-button-secondary skin-action-button"
                style={{ minWidth: '120px' }}
                disabled={isProceeding}
                onClick={handleExitCapture}
              >
                Back
              </button>
              <button
                className="skin-action-button-secondary skin-action-button"
                style={{ minWidth: '120px' }}
                disabled={isProceeding}
                onClick={() => {
                  setHairCaptures({ crown: null, left: null });
                  setMagnifiedScalp(null);
                  setCaptureStep('crown');
                  captureStepRef.current = 'crown';
                  hasAutoCapturedRef.current = false;
                  startFrameLoop();
                }}
              >
                Retake
              </button>
              <button
                type="button"
                className="hair-action-button hair-action-button-premium"
                style={{}}
                onClick={() => void handleProceedToAnalysis()}
                disabled={isProceeding}
              >
                {isProceeding ? 'Analyzing…' : 'Confirm & Analyze'}
              </button>
              {saveError && (
                <p className="face-detection-proceed-error" role="alert">
                  {saveError}
                </p>
              )}
            </div>

            {isProceeding && (
              <div className="face-detection-loading-overlay" role="status" aria-live="polite" aria-label="Preparing skin analysis">
                <div className="face-detection-loading-content">
                  <div className="loading-spinner" aria-hidden="true" />
                  <p className="loading-text">Preparing your hair analysis...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info Modal for Perfect Results Tips */}
        {showInfoModal && (
          <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)}>
            <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="info-modal-header">
                <h2 className="info-modal-title">Tips for Perfect Results</h2>
                <button
                  className="info-modal-close-button"
                  onClick={() => setShowInfoModal(false)}
                  aria-label="Close tips"
                >
                  ×
                </button>
              </div>

              <div className="info-modal-body">
                <div className="info-tips-list">
                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Position Your Face</h3>
                      <p className="info-tip-text">Center your face within the guide frame. Keep your face straight and look directly at the camera.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Good Lighting</h3>
                      <p className="info-tip-text">Ensure you have front-facing, even lighting across your face. Avoid shadows and backlighting.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Remove Accessories</h3>
                      <p className="info-tip-text">Remove glasses, hats, or any accessories that might obstruct your face. Pull your hair back if needed.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Maintain Distance</h3>
                      <p className="info-tip-text">Keep an appropriate distance from the camera - not too close, not too far. The guide will help you position correctly.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Stay Still</h3>
                      <p className="info-tip-text">Once you see the green guide and countdown starts, hold still until the photo is captured automatically.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Natural Expression</h3>
                      <p className="info-tip-text">Keep a natural, relaxed expression. Avoid smiling too much or making exaggerated facial expressions.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                        <path d="M8 12h.01M12 12h.01M16 12h.01"></path>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Lighting & Makeup</h3>
                      <p className="info-tip-text">Results may vary depending on lighting conditions and if makeup is applied. For best accuracy, use natural lighting and avoid heavy makeup.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-modal-footer">
                <button
                  className="skin-action-button"
                  style={{ maxWidth: '200px', margin: '0 auto' }}
                  onClick={() => setShowInfoModal(false)}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <PageFooter />
    </div>
  );
};

export default HairCapturePage;

